// Who may edit a story board, and which artifact the editor opens (#1416).
// The server is the source of truth (the story routes answer 404 to anyone
// else); these decide only whether the Edit button is drawn.

/** The session owner or a Manager may edit a story board. Emails compare case-insensitively. */
export function canEditStoryBoard(isManager: boolean, sessionOwner: string | undefined, userEmail: string | undefined): boolean {
  if (isManager) return true
  if (!sessionOwner || !userEmail) return false
  return sessionOwner.trim().toLowerCase() === userEmail.trim().toLowerCase()
}

/** A web app artifact as the frontend sees it: either the SSE/chat-ui shape or the API's. */
interface ChatArtifactLike {
  id?: string
  filePath: string
  artifactType: string
  isDir?: boolean
}

/**
 * The artifact the editor opens, by id. The captured directory row wins over
 * an entry-file row that adopted the same id (mergeDbAndSseArtifacts), so the
 * frame loads `<dir>/index.html` either way.
 */
export function findStoryArtifact<T extends ChatArtifactLike>(artifacts: T[], artifactId: string): T | undefined {
  const matches = artifacts.filter(a => a.id === artifactId && a.artifactType === 'webapp')
  return matches.find(a => a.isDir) ?? matches[0]
}

/** An artifact row as GET /agent/session/:id/artifacts returns it. */
export interface ApiArtifactRow {
  id: string
  file_path: string
  artifact_type: string
  is_dir?: boolean
  user_email?: string
}

const trimDir = (p: string) => p.replace(/^\/+/, '').replace(/\/+$/, '')

/**
 * The story board a published web app dashboard shows: the session's web app
 * directory artifact whose path is the dashboard's `dist_path`. A dashboard
 * published before `dist_path` was recorded resolves only when the session has
 * exactly one web app (the same rule the server's Publish uses).
 */
export function pickDashboardStoryArtifact(rows: ApiArtifactRow[], distPath: string | undefined): ApiArtifactRow | null {
  const webapps = rows.filter(r => r.artifact_type === 'webapp' && r.is_dir)
  if (distPath) {
    const want = trimDir(distPath)
    return webapps.find(r => trimDir(r.file_path) === want) ?? null
  }
  return webapps.length === 1 ? webapps[0] : null
}
